<script>
  export default {
    title: 'Project 3: List Structure Exercises',
    starterId: 'proj3.zip'
  }
</script>

<template>
  <section>

    <p>This programming assignment gives you practice with using
      pointers�particularly the this pointer�and building and using
      linked lists and trees. In addition, it provides you with experience in using
      recursion and designing constructors, and introduces you to destructors.</p>

    <Readings :readings="[
      {
        title: '<cite>A Computer Science Tapestry</cite> Sections 12.1 - 12.2',
        link: 'http://www.cs.duke.edu/csed/tapestry/'
      }
    ]"/>

    <article>
      <h2>Starter Code</h2>

      <p>Please download the starter code from above.</p>
    </article>

    <article>
      <h2>Your Task</h2>
      <p>Do <em>all three</em> of the programming exercises described below.</p>
    </article>

    <article>
      <h2>Miscellaneous Information</h2>

      <p>The null pointer is variously referred to in this document and the
        textbooks as <code>NULL</code> (an identifier defined in
        <code>&lt;cstdlib&gt;</code>) or 0. Bjarne Stroustrup (inventor of C++)
        recommends using 0, since <code>NULL</code> is not necessarily
        type-compatible with all pointers.</p>

      <p>Incidentally, the C++ standard library provides implementations for most
        of the data structures one would ever want to use, including those designed
        from scratch for this assignment. A good online reference for the standard
        library, should you go on to do more C++ programming, is
        <code>http://www.sgi.com/Technology/STL.</code></p>
    </article>

    <hr>

    <article>
      <h2>Exercise 1: List Destructor</h2>

      <p>The files <code>lists.h</code> and <code>lists.cpp</code> implement a list data type
        that represents a list as a pointer to a <code>ListNode</code>, similar to what's
        described in <cite>A Computer Science Tapestry</cite> Section 12.2.</p>

      <p>The <code>ListNode</code> class has a disadvantage that you deal with in this exercise.
        Its destructor function, called when a <code>ListNode</code> is deleted, results in an
        infinite loop. You are to discover why, and fix it, changing as little code as possible.
        (In particular, you're not allowed to change <code>lists.h</code>.) The corrected
        destructor should result not only in the node itself being deleted, but in deletion of
        all nodes linked to that node.</p>

      <p>Here's an example. The version of <code>~ListNode</code> that produced
        the output below included an output statement to show what was going on,
        and your version should also.</p>

      <p>Test your program using <code>destr.test.cpp</code>. First create a test file with content:</p>

      <pre v-highlightjs><code class="plain">3 4 5</code></pre>

      <p>Result of executing the program:</p>

      <pre v-highlightjs><code class="bash">$ <b>g++ -g -Wall -o destr.test destr.test.cpp lists.cpp</b>
$ <b>destr.test &lt; testfile</b>
5 4 3
Deleting node with value 5
Deleting node with value 4
Deleting node with value 3</code></pre>

      <article>
        <h3>Checklist</h3>
        <ul>
          <li>Explanation of the problem with the existing code.</li>
          <li>Corrected code, with as few changes as possible, tested as specified.</li>
          <li>Adherence to CS 9F style standards:
            <ul>
              <li>appropriate use of indenting and white space;</li>
              <li>avoidance of "forbidden C++";</li>
              <li>variable and function names that reflect their use;</li>
              <li>informative comments at the head of each function;</li>
              <li>no routine more than twenty-four lines of code.</li>
            </ul>
          </li>
        </ul>
      </article>
    </article>

    <hr>

    <article>
      <h2>Exercise 2: Circularly Linked Lists</h2>

      <article>
        <h3>Background</h3>

        <p>The file <code>selection.cpp</code> contains most of a program that implements
          the following method of selecting a person from a group of <code>n</code> people:</p>

        <ol>
          <li>All the people stand in a circle. Someone is designated as the starting person, and that person is given a marble.</li>
          <li>The starting person thinks of a positive number <code>k</code> less than <code>n</code>.</li>
          <li>The marble is passed from one person to the next on the circle, <code>k</code> times.</li>
          <li>The person now holding the marble is "out", and his or her successor on the circle takes the marble.</li>
          <li>Steps c and d are repeated until only one person remains in the circle. That's the selected person.</li>
        </ol>

        <p>Here's an example. Suppose there are 8 people, <code>k</code> = 2, and
          person #1 is the starting person. The diagram below illustrates the seven
          repetitions of steps c and d. Arrows represent passes of the marble, and
          the shaded box indicates the person who goes out at each step.</p>

        <table class="table table-bordered">
          <tbody>
          <tr>
            <td>
              <figure>
                <img src="./images/marble-1.gif" class="img-responsive center-block">
                <figcaption>Step 1: #1 starts; #3 is eliminated.</figcaption>
              </figure>
            </td>
            <td>
              <figure>
                <img src="./images/marble-2.gif" class="img-responsive center-block">
                <figcaption>Step 2: #4 takes the marble; #6 is eliminated.</figcaption>
              </figure></td>
            <td>
              <figure>
                <img src="./images/marble-3.gif" class="img-responsive center-block">
                <figcaption>Step 3: #7 takes the marble; #1 is eliminated.</figcaption>
              </figure></td>
            <td>
              <figure>
                <img src="./images/marble-4.gif" class="img-responsive center-block">
                <figcaption>Step 4: #2 takes the marble; #5 is eliminated.</figcaption>
              </figure></td>
          </tr>
          <tr>
            <td>
              <figure>
                <img src="./images/marble-5.gif" class="img-responsive center-block">
                <figcaption>Step 5: #7 takes the marble; #2 is eliminated.</figcaption>
              </figure>
            </td>
            <td>
              <figure>
                <img src="./images/marble-6.gif" class="img-responsive center-block">
                <figcaption>Step 6: #4 takes the marble; #8 is eliminated.</figcaption>
              </figure></td>
            <td>
              <figure>
                <img src="./images/marble-7.gif" class="img-responsive center-block">
                <figcaption>Step 7: #4 takes the marble and passes it to #7, who passes it back; #4 is thus eliminated.</figcaption>
              </figure></td>
            <td>
            </td>
          </tr>
          </tbody></table>
      </article>

      <article>
        <h3>Exercise</h3>

        <p>The program in <code>selection.cpp</code> uses a circularly linked list structure, partially implemented in the files <code>CLLNode.h</code> and <code>CLLNode.cpp</code>, to represent the candidates for selection. Two member functions for the <code>CLLNode</code> class, <code>insert</code> and <code>remove</code>, have not been supplied. You are to provide definitions for these functions that agree with the comments in the supplied code, and complete <code>selection.cpp</code> by supplying the appropriate calls to <code>insert</code> and <code>remove</code>.</p>

        <p>The constant definitions in <code>selection.cpp</code> are set up to test your code on the example above. Bring the output of the program to a tutor for grading.</p>
      </article>

      <article>
        <h3>Checklist</h3>
        <ul>
          <li>Correct implementations of <code>insert</code> and <code>remove</code>, tested as specified.</li>
          <li>Listings of program text, tests, and test results.</li>
          <li>Adherence to CS 9F style standards:
            <ul>
              <li>appropriate use of indenting and white space;</li>
              <li>avoidance of "forbidden C++";</li>
              <li>variable and function names that reflect their use;</li>
              <li>informative comments at the head of each function;</li>
              <li>no routine more than twenty-four lines of code.</li>
            </ul>
          </li>
        </ul>
      </article>
    </article>

    <hr>

    <article>
      <h2>Exercise 3: Amoeba Family Trees</h2>

      <article>
        <h3>Background</h3>

        <p>An amoeba family tree is simpler than a normal family tree because
          amoebas do not get married. An amoeba has one parent, perhaps a million
          siblings (brothers and sisters), and billions and billions of children.
          An amoeba also has a name.</p>

        <p>Amoebas (or amoebae) live dull lives. All they do is reproduce. So all
          we need to keep track of them are the following declarations:</p>

        <pre v-highlightjs><code class="c">class Amoeba
{
public:
    Amoeba(<u style="color:red">_____</u>string);       // birth of an amoeba

    <u style="color:red">_____</u>string Name();        // returns your name

    Amoeba* Parent();          // returns your parent

    void AddChild(Amoeba*);    // add a baby amoeba to the family

private:
    <u style="color:red">_____</u>string myName;        // this amoeba's name

    Amoeba* myParent;          // good old mom (or is it dad?)

    Amoeba* myOlderSibling;    // the next older brother/sister

    Amoeba* myYoungestChild;   // the youngest kid

    Amoeba* myOldestChild;     // the oldest kid
};</code></pre>

        <p>
          The above code is the content of <code>amoebas.h</code>; the member functions are in
          <code>amoebas.cpp</code>. A program that uses these functions is in the file
          <code>amoebamain.cpp</code>.
        </p>
        <p>
          <b>NOTE</b>: The header as it shown normally won't compile using a modern C++ compiler.
          Can you figure out why? (Hint: it has something to do with the <u style="color:red">_____</u>
          lines above. Hint#2: <a href="http://www.cplusplus.com/doc/tutorial/namespaces/">Check
          out this article if you are still stuck.</a>)
        </p>

        <article>
          <h3>Exercise</h3>

          <p>This exercise has five parts.</p>

          <ol type="1">
            <li>
              <p>
                Draw a diagram of the "family" constructed by the program in <code>amoebamain.cpp</code>.
                Use arrows in the diagram to show all the family relationships. Make sure that your
                drawing shows the tree <i>exactly</i> as the program builds it (a misleading drawing
                is worse than none at all).
              </p>
            </li>
            <li>
              <p>
                Suppose you were the amoeba represented by the pointer <code>me</code>. Add a statement
                to <code>amoebamain.cpp</code> to print the name of your grandparent, who should be Amos
                McCoy. Try it out, and be prepared to explain to a tutor what happens and what's necessary
                to make the statement work properly. (Hint: look at your drawing.)
              </p>
            </li>
            <li>
              <p>
                Add a member function called <code>PrintChildren</code> to <code>amoebas.cpp</code> that
                prints the names of the amoeba's children, one per line. (Also add its prototype to
                <code>amoebas.h</code>.) Test your function by adding a call or two to the end of
                <code>amoebamain.cpp</code>, and show the results of your tests to a tutor for grading.
              </p>
            </li>
            <li>
              <p>
                Add a member function called <code>PrintGrandchildren</code> to <code>amoebas.cpp</code>
                that prints the names of the amoeba's grandchildren, one per line. (Also add its prototype
                to <code>amoebas.h</code>.) Your function should use the <code>PrintChildren</code>
                function described above. Test your function by adding a call or two to the end of
                <code>amoebamain.cpp</code>-you may also need to add some amoebas to the tree-and show the
                results of your tests to a tutor for grading.
              </p>
            </li>
            <li>
              <p>
                Add a member function called <code>PrintDescendants</code> to <code>amoebas.cpp</code>
                that prints the names of all the amoeba's descendants. (Also add its prototype to
                <code>amoebas.h</code><code>.) </code>One name should be printed per line. The names
                of the children of each descendant amoeba should be printed on the lines immediately
                following; they should be indented four spaces more than the name of their parent.
                Thus the output should appear in the form
              </p>

              <pre v-highlightjs><code class="plain">child-name
    grandchild-name
        great-grandchild-name
            great-great-grandchild-name
    grandchild-name
child-name
    grandchild-name
child-name
child-name
    grandchild-name</code></pre>
            </li>
          </ol>

          <p>
            Test your function by adding a call or two to the end of <code>amoebamain.cpp</code>, and show
            the results of your tests to a tutor for grading. You may add private helper functions to the
            <code>Amoeba</code> class if you want.
          </p>
          <p>
            Your <code>PrintChildren</code>, <code>PrintGrandchildren</code>, and <code>PrintDescendants</code>
            member functions should all work no matter which amoeba they are called with.
          </p>
        </article>

        <article>
          <h3>Checklist</h3>
          <ul>
            <li>Correct diagram.</li>
            <li>Correct additions to <code>amoebas.h</code> and <code>amoebas.cpp</code> (statement to print the name of <code>me</code>'s grandparent, plus member functions <code>PrintChildren</code>, <code>PrintGrandchildren</code>, and <code>PrintDescendants</code>), tested as specified.</li>
            <li>Listings of program text, tests, and test results.</li>
            <li>Adherence to CS 9F style standards:
              <ul>
                <li>appropriate use of indenting and white space;</li>
                <li>avoidance of "forbidden C++";</li>
                <li>variable and function names that reflect their use;</li>
                <li>informative comments at the head of each function;</li>
                <li>no routine more than twenty-four lines of code.</li>
              </ul>
            </li>
          </ul>
        </article>
      </article>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/cpp/ListStructureExercises.vue